import { useState, useMemo, useEffect, useCallback } from "react";
import api from "api/api";

//Material UI Components
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";

// import mui icons
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

//import component
import Loader from "../common/Loader";

// assets
import enableDeleteIcon from "../../assets/icons/enableDeleteIcon.png";
import disableDeleteIcon from "../../assets/icons/disableDeleteIcon.png";

//other packages
import { AgGridReact } from "ag-grid-react";

//redux
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

function StudyMethodTable() {
    const dispatch = useDispatch();

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [studyTypeMethodFormData, setStudyTypeMethodFormData] = useState({ id: "", title: "", description: "", study_type: "" });
    const [deleteRowData, setDeleteRowData] = useState(null);

    const [fetchFlag, setFetchFlag] = useState(true);

    const editCellRenderer = (row) => {
        return (
            <EditIcon
                color="primary"
                style={{
                    cursor: "pointer",
                }}
                onClick={() => handleUpdateStudyTypeMethod(row)}
            />
        );
    };

    const deleteCellRenderer = (row) => {
        const isDisabled = row.data?.core_data_model_exist === true;

        return (
            <button
                type="button"
                onClick={() => {
                    if (!isDisabled) {
                        handleDeleteMethod(row);
                    }
                }}
                disabled={isDisabled}
                style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.5 : 1,
                }}
            >
                <img
                    src={isDisabled ? disableDeleteIcon : enableDeleteIcon}
                    alt="Delete"
                    style={{ width: "22px", height: "22px" }}
                />
            </button>
        );
    };

    const columnDefs = [
        {
            headerName: "Study Type Method",
            minWidth: 350,
            field: "title",
        },
        {
            headerName: "Study Type",
            minWidth: 300,
            field: "study_type_title",
        },
        {
            headerName: "Description",
            minWidth: 550,
            field: "description",
        },
        {
            headerName: "Action",
            minWidth: 220,
            filter: false,
            cellRenderer: editCellRenderer,
        },
        {
            headerName: "Delete",
            minWidth: 220,
            filter: false,
            cellRenderer: deleteCellRenderer,
        },
    ];

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            const url = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/all/?product_category=${productCategoryId}`;
            api.get(url)
                .then(async (res) => {
                    setData(res.data);
                    setIsLoading(false);
                    setFetchFlag(false);
                })
                .catch(async (error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        }
        if (fetchFlag) {
            fetchData();
        }
    }, [productCategoryId, fetchFlag]);

    const sortingOrder = useMemo(() => {
        return ["desc", "asc", null];
    }, []);

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            suppressMenu: true,
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
            cellStyle: { fontSize: "12px", border: "none" },
            minWidth: 490,
        };
    }, []);

    const handleUpdateStudyTypeMethod = (selectedRow) => {
        const obj = {
            id: selectedRow.data?.id,
            title: selectedRow.data?.title,
            description: selectedRow.data?.description,
            study_type: selectedRow.data?.study_type_title,
        };
        setStudyTypeMethodFormData(obj);
        setShowDialog(true); // open dialog
    };

    const handleDeleteMethod = (selectedRow) => {
        setDeleteRowData(selectedRow.data);
        setShowDeleteDialog(true);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    const handleCloseDeleteDialog = () => {
        setShowDeleteDialog(false);
        setDeleteRowData(null);
    };

    const handleInputChangeStudyTypeMethod = useCallback((e, name) => {
        let _newValue = null;
        if (name === "template") {
            _newValue = e;
        } else {
            _newValue = e.target.value;
        }
        setStudyTypeMethodFormData((prevState) => ({
            ...prevState,
            [name]: _newValue,
        }));
    }, []);

    const handleSubmitStudyTypeMethod = async () => {
        const payload = {
            title: studyTypeMethodFormData?.title,
            description: studyTypeMethodFormData?.description,
        };
        const id = studyTypeMethodFormData?.id;
        const url = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/${id}/`;

        try {
            await api
                .put(url, payload)
                .then(async () => {
                    setShowDialog(false);
                    handleError("success", { message: "Study Type method has been updated successfully" });
                    setFetchFlag(true);
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteRowData?.id) return;
        const payload = {
            archive: true,
        };
        const url = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/${deleteRowData.id}/`;

        try {
            await api
                .put(url, payload)
                .then(async () => {
                    setShowDeleteDialog(false);
                    handleError("success", { message: "Study Type method has been deleted successfully" });
                    setFetchFlag(true);
                    setDeleteRowData(null);
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    };

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    return (
        <div>
            {isLoading && <Loader />}

            <div className="landing-page-table mb-0">
                <div
                    className="ag-theme-alpine"
                    style={{ height: "calc(100vh - 205px)", width: "100%" }}
                >
                    <AgGridReact
                        rowStyle={{
                            background: "white",
                        }}
                        defaultColDef={defaultColDef}
                        rowData={data}
                        columnDefs={columnDefs}
                        onGridReady={(params) => params.id}
                        // sideBar={true}
                        rowHeight={38}
                        headerHeight={40}
                        suppressDragLeaveHidesColumns={true}
                        sortingOrder={sortingOrder}
                    ></AgGridReact>
                </div>
            </div>
            {showDialog && (
                <Dialog
                    open={showDialog}
                    // onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle
                        style={{
                            backgroundColor: "rgb(51, 94, 234,0.1)",
                            color: "rgb(51, 94, 234)",
                            fontSize: 15,
                            textAlign: "center",
                            padding: "10px 12px",
                        }}
                        id="dialog-title"
                    >
                        Edit Study Type Method
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 2,
                                color: "rgb(51, 94, 234) !important",
                            }}
                            onClick={handleCloseDialog}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ py: 2, px: 4 }}>
                        <div className="stm-form-group">
                            <label htmlFor="title">Study Type Method Title:</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={studyTypeMethodFormData?.title}
                                onChange={(e) => handleInputChangeStudyTypeMethod(e, "title")}
                            />
                        </div>
                        <div className="stm-form-group">
                            <label htmlFor="title">Study Type:</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={studyTypeMethodFormData?.study_type}
                                disabled={true}
                            />
                        </div>
                        <div className="stm-form-group">
                            <label htmlFor="description">Description:</label>
                            <input
                                type="text"
                                id="description"
                                name="description"
                                value={studyTypeMethodFormData?.description}
                                onChange={(e) => handleInputChangeStudyTypeMethod(e, "description")}
                            />
                        </div>

                        <div style={{ justifyContent: "center", display: "flex", marginTop: 20 }}>
                            <Button
                                variant="outlined"
                                className="custom-btn custom-btn--upload-reset"
                                onClick={handleCloseDialog}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                className="custom-btn custom-btn--upload-reset"
                                onClick={handleSubmitStudyTypeMethod}
                            >
                                Update
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && (
                <Dialog
                    open={showDeleteDialog}
                    // onClose={handleCloseDeleteDialog}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogContent sx={{ py: 2, px: 4 }}>
                        <div style={{ textAlign: "center", marginBottom: "20px" }}>
                            <p style={{ fontSize: "12px", marginBottom: "10px", fontWeight: "600" }}>Are you sure you want to delete this study type method?</p>
                        </div>

                        <div style={{ justifyContent: "center", display: "flex", marginTop: 20, gap: "10px" }}>
                            <Button
                                variant="outlined"
                                className="custom-btn custom-btn--upload-reset"
                                onClick={handleCloseDeleteDialog}
                            >
                                Disagree
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                className="custom-btn custom-btn--upload-reset"
                                onClick={handleConfirmDelete}
                                style={{
                                    backgroundColor: "white",
                                    color: "#f44336",
                                    borderColor: "#f44336",
                                    borderWidth: "2px",
                                }}
                            >
                                Agree
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

export default StudyMethodTable;
